import { Link } from 'react-router-dom';
import JsonLd from '@/components/base/JsonLd';

interface ArticleLayoutProps {
  title: string;
  subtitle: string;
  featuredImage: string;
  featuredImageAlt: string;
  articleDate?: string;
  readingTime?: string;
  category?: string;
  children: React.ReactNode;
  ctaText?: string;
  jsonLd?: Record<string, unknown>;
}

export default function ArticleLayout({
  title,
  subtitle,
  featuredImage,
  featuredImageAlt,
  articleDate = 'August 4, 2026',
  readingTime = '8 minutes',
  category = 'Sugargoo Spreadsheet',
  children,
  ctaText = 'Visit Main Platform',
  jsonLd,
}: ArticleLayoutProps) {
  return (
    <main>
      {jsonLd && <JsonLd data={jsonLd} />}
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md shadow-sm">
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-heading text-lg md:text-xl font-semibold text-foreground-950">
                Sugargoo<span className="text-primary-500">Spreadsheet</span>
              </span>
            </Link>
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                ← Home
              </Link>
              <Link
                to="/sugargoo-spreadsheet-guide"
                className="hidden sm:inline-flex text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                Shopping Guide
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-sm bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap px-4 py-1.5 rounded-full cursor-pointer"
              >
                Shop Now
              </a>
            </div>
          </div>
        </div>
      </nav>

      <article className="py-10 md:py-16">
        <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
          <header className="mb-10">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Link to="/" className="text-xs text-primary-500 hover:text-primary-600 transition-colors">
                Home
              </Link>
              <span className="text-xs text-foreground-400">/</span>
              <Link to="/sugargoo-spreadsheet-guide" className="text-xs text-foreground-500 hover:text-primary-500 transition-colors">
                Shopping Guide
              </Link>
              <span className="text-xs text-foreground-400">/</span>
              <span className="text-xs text-foreground-500">{title.length > 40 ? title.slice(0, 40) + '...' : title}</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              {title}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-4">
              {subtitle}
            </p>
            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <span>Updated: {articleDate}</span>
              <span>•</span>
              <span>Reading time: {readingTime}</span>
              <span>•</span>
              <span>Category: {category}</span>
            </div>
          </header>

          <div className="relative w-full h-[280px] md:h-[380px] rounded-2xl overflow-hidden mb-10">
            <img
              src={featuredImage}
              alt={featuredImageAlt}
              className="w-full h-full object-cover object-center"
            />
          </div>

          <div className="prose-custom space-y-6 text-foreground-700 leading-relaxed text-base">
            {children}

            <div className="bg-primary-50/50 rounded-2xl p-6 md:p-8 my-8 border border-primary-100/50">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-3">
                Ready to Start Shopping?
              </h2>
              <p className="text-sm md:text-base text-foreground-600 mb-5">
                Browse our complete category collection on the main platform and find exactly what you are looking for with the latest Sugargoo Spreadsheet data.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm px-6 py-3 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  {ctaText}
                  <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 border border-primary-300 text-primary-600 hover:bg-primary-50 font-semibold text-sm px-6 py-3 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  ← Back to Home
                </Link>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-background-200">
              <p className="text-sm text-foreground-500 mb-4">
                This article was last updated on {articleDate}. For the latest Sugargoo Spreadsheet resources and updates, visit our <Link to="/" className="text-primary-500 hover:text-primary-600 underline">homepage</Link> or explore product categories directly on <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-500 hover:text-primary-600 underline">kaspreadsheet.com</a>.
              </p>
            </div>
          </div>
        </div>
      </article>

      <footer className="bg-secondary-100 border-t border-secondary-200/60 py-8">
        <div className="w-full max-w-3xl mx-auto px-4 md:px-6 text-center">
          <Link to="/" className="inline-block mb-3">
            <span className="font-heading text-lg font-semibold text-foreground-950">
              Sugargoo<span className="text-primary-500">Spreadsheet</span>
            </span>
          </Link>
          <p className="text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} Sugargoo Spreadsheet Hub. All rights reserved.
          </p>
        </div>
      </footer>
    </main>
  );
}